import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Typography, Checkbox, Input, Button, Space, Radio, Modal, notification, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import moment from 'moment';
import AddIssueModal from './AddIssueModal';
import './DSR.css';
import {
    fetchEhsLog,
    updateEhsLog,
    addEhsLogItem,
    deleteEhsLogItem,
    fetchAllEhsIssues,
    updateDsrLogSections
} from '../../redux/dsr/action';

const { Title, Text } = Typography;

const EHSSection = ({
    shift,
    selectedDate,
    onComplete,
    onCancel,
    selectedProject,
    selectedMainline,
    currentDsrLog,
    isSkipped,
    isSubmitted,
    isShiftSkipped = false,
    onSkipChange,
    // Redux props
    currentEhsLog,
    allEhsIssues,
    isEhsLogLoading,
    isUpdatingEhsLog,
    isAddingEhsLogItem,
    isDeletingEhsLogItem,
    onFetchEhsLog,
    onUpdateEhsLog,
    onAddEhsLogItem,
    onDeleteEhsLogItem,
    onFetchAllEhsIssues,
    onUpdateDsrLogSections
}) => {
    const [skipped, setSkipped] = useState(false);
    const [issues, setIssues] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const displayDate = selectedDate ? moment(selectedDate).format('DD-MMM-YYYY') : moment().format('DD-MMM-YYYY');

    const getShiftNumber = (shiftStr) => (shiftStr === 'day' ? 0 : 1);

    useEffect(() => {
        if (selectedProject && selectedMainline && selectedDate && !isSkipped) {
            const userEmail = JSON.parse(localStorage.getItem('user')).email;
            const logDate = moment(selectedDate).format('YYYY-MM-DD');
            const shiftNumber = getShiftNumber(shift);
            onFetchEhsLog({
                projectId: selectedProject.id,
                logDate,
                userEmail,
                shift: shiftNumber,
                sowId: selectedMainline.id,
            });
            onFetchAllEhsIssues();
        }
    }, [selectedProject, selectedMainline, selectedDate, shift, isSkipped, onFetchEhsLog, onFetchAllEhsIssues]);

    useEffect(() => {
        if (currentEhsLog?.data) {
            setIssues(currentEhsLog.data.map(item => ({
                ...item,
                severity: item.major ? 'major' : (item.minor ? 'minor' : null)
            })));
            setIsDirty(false);
        }
    }, [currentEhsLog]);

    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped]);

    useEffect(() => {
        setIsDirty(false);
    }, [selectedDate]);

    // Reset skip state when section or shift changes
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped, shift, selectedDate]);

    // Clear local state when component unmounts
    useEffect(() => {
        return () => {
            setIssues([]);
            setSkipped(false);
            setIsDirty(false);
            setIsAddModalVisible(false);
            setSelectedRowKeys([]);
        };
    }, []);

    const handleIssueChange = (id, field, value) => {
        setIssues(prevIssues =>
            prevIssues.map(item => {
              if (item.id !== id) return item;
        
              // Handle severity changes
              if (field === 'severity') {
                return {
                  ...item,
                  severity: value, // still keep for UI convenience if needed
                  major: value === 'major' ? 1 : 0,
                  minor: value === 'minor' ? 1 : 0,
                };
              }
        
              // Handle other field changes (e.g., comments)
              return { ...item, [field]: value };
            })
          );
        setIsDirty(false);
    };

    const handleAddIssue = (ehsIssueId) => {
        onAddEhsLogItem({
            ehsIssueId,
            projectId: selectedProject.id,
            dsrLogId: currentDsrLog.id,
            shift: getShiftNumber(shift),
            // Pass params for refetch
            logDate: moment(selectedDate).format('YYYY-MM-DD'),
            userEmail: JSON.parse(localStorage.getItem('user')).email,
            sowId: selectedMainline.id,
        });
        setIsAddModalVisible(false);
    };

    const handleDeleteSelected = () => {
        selectedRowKeys.forEach(id => {
            onDeleteEhsLogItem({
                id,
                projectId: selectedProject.id,
                // Pass params for refetch
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id,
            });
        });
        setSelectedRowKeys([]);
    };

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    const handleSave = async () => {
        if (skipped) {
            // If skipped, just complete without saving data to API
            if (onComplete) onComplete(true, 'ehs');
            return;
        }

        // Prepare data for API - save all issues that have been modified or have data
        const dsrLogEhsIssues = issues
            .filter(item => {
                // Save issues that are selected OR have been modified (have severity or comments)
                return selectedRowKeys.includes(item.id) || item.severity || item.comments;
            })
            .map(item => ({
                id: item.id,
                major: item.severity === 'major' ? 1 : 0,
                minor: item.severity === 'minor' ? 1 : 0,
                comments: item.comments || '',
                dsrLogId: currentDsrLog?.id,
                shift: getShiftNumber(shift)
            }));


        const payload = {
            dsrLogEhsIssues,
            // Pass params for refetch
            projectId: selectedProject.id,
            logDate: moment(selectedDate).format('YYYY-MM-DD'),
            userEmail: JSON.parse(localStorage.getItem('user')).email,
            sowId: selectedMainline.id,
            shift: getShiftNumber(shift),
        };

        try {
            await onUpdateEhsLog(payload);
            setIsDirty(false);

            // Update submission status and sectionsSkipped for this section
            const submissionStatus = generateSubmissionStatus('ehs', shift, true);
            const sectionsSkipped = {
                ...currentDsrLog.sectionsSkipped,
                isEhsSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isEhsSkippedDayShift || 0),
                isEhsSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isEhsSkippedNightShift || 0)
            };

            await onUpdateDsrLogSections({
                dsrLogId: currentDsrLog?.id,
                submissionStatus: submissionStatus,
                sectionsSkipped: sectionsSkipped
            });

            // Only navigate to next section on successful save
            if (onComplete) onComplete(true, 'ehs');

            // Refresh data after successful save
            setTimeout(() => {
                if (selectedProject && selectedMainline && selectedDate) {
                    const userEmail = JSON.parse(localStorage.getItem('user')).email;
                    const logDate = moment(selectedDate).format('YYYY-MM-DD');
                    const shiftNumber = shift === 'day' ? '0' : '1';

                    onFetchEhsLog({
                        projectId: selectedProject.id,
                        logDate: logDate,
                        userEmail: userEmail,
                        shift: shiftNumber,
                        sowId: selectedMainline.id
                    });
                }
            }, 100);

        } catch (error) {
            console.error('Error saving EHS log:', error);
            notification.error({
                message: 'Save Failed',
                description: 'Failed to save EHS data. Please try again.'
            });
            // Stay in the same section on error - button will be re-enabled
        }
    };

    const handleSkipChange = (e) => {
        console.log('handleSkipChange', e);
        const newSkipState = e.target.checked;
        setSkipped(newSkipState);
        setIsDirty(true);
        console.log('currentDsrLog', currentDsrLog);

        // Use the parent component's centralized skip handler
        if (onSkipChange) {
            onSkipChange(e);
        } else {
            // Fallback to local handling if no parent handler provided
            if (currentDsrLog?.id) {
                const submissionStatus = generateSubmissionStatus('ehs', shift, true);
                const sectionsSkipped = {
                    ...currentDsrLog.sectionsSkipped,
                    isEhsSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isEhsSkippedDayShift || 0),
                    isEhsSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isEhsSkippedNightShift || 0),
                };

                onUpdateDsrLogSections({
                    dsrLogId: currentDsrLog.id,
                    submissionStatus: submissionStatus,
                    sectionsSkipped: sectionsSkipped
                });
            }
        }
    };

    const handleNext = () => {
        if (isDirty) {
            Modal.confirm({
                title: 'Warning!',
                icon: <ExclamationCircleOutlined />,
                content: 'You have made changes. Do you want to save them?',
                okText: 'Save',
                cancelText: 'Discard',
                onOk: () => {
                    handleSave();
                    if (onComplete) onComplete(!skipped);
                },
                onCancel: () => {
                    if (onComplete) onComplete(!skipped);
                },
            });
        } else {
            if (onComplete) onComplete(!skipped);
        }
    };

    return (
        <div style={{ padding: 24, position: 'relative' }}>
            <Spin
                spinning={isEhsLogLoading}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                tip="Loading EHS data..."
                size="large"
            >
                {isShiftSkipped ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        backgroundColor: 'rgba(250, 173, 20, 0.1)',
                        border: '1px solid #faad14',
                        borderRadius: 8,
                        marginBottom: 24
                    }}>
                        <div style={{
                            fontSize: 18,
                            fontWeight: 600,
                            color: '#faad14',
                            marginBottom: 8
                        }}>
                            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
                        </div>
                        <div style={{
                            fontSize: 14,
                            color: '#666'
                        }}>
                            This section is disabled because the {shift} shift has been skipped.
                        </div>
                    </div>
                ) : (
                    <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <div></div>
                                <div className="dsr-section-icons">
                                    <Button
                                        shape="circle"
                                        icon={<PlusOutlined style={{ color: '#ff4d4f' }} />}
                                        onClick={() => setIsAddModalVisible(true)}
                                        disabled={skipped || isSubmitted}
                                        className="dsr-section-icon-button"
                                    />
                                    <Button
                                        shape="circle"
                                        icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                                        disabled={selectedRowKeys.length === 0 || skipped || isSubmitted}
                                        onClick={handleDeleteSelected}
                                        className="dsr-section-icon-button"
                                    />
                                </div>
                            </div>

                            <Row gutter={16} style={{ marginBottom: 8, padding: '0 8px' }}>
                                <Col span={2}><Text strong></Text></Col>
                                <Col span={10}><Text strong>Issues</Text></Col>
                                <Col span={3}><Text strong>Major</Text></Col>
                                <Col span={3}><Text strong>Minor</Text></Col>
                                <Col span={6}><Text strong>Comments</Text></Col>
                            </Row>

                            <Checkbox.Group style={{ width: '100%' }} value={selectedRowKeys} onChange={setSelectedRowKeys}>
                                {issues.map(item => (
                                    <Row key={item.id} gutter={16} align="middle" style={{ marginBottom: 12, padding: '0 8px' }}>
                                    <Col span={2}>
                                      <Checkbox
                                        value={item.id}
                                        disabled={skipped || isSubmitted}
                                        className="red-checkbox"
                                        style={{ color: '#ff4d4f', '--antd-wave-shadow-color': '#ff4d4f' }}
                                      />
                                    </Col>
                                  
                                    <Col span={10}>
                                      <Text>{item.name}</Text>
                                    </Col>
                                  
                                    {/* Major and Minor columns aligned under their headers */}
                                    <Col span={6}>
                                      <Radio.Group
                                        className="red-radio"
                                        onChange={(e) => handleIssueChange(item.id, 'severity', e.target.value)}
                                        value={item.severity}
                                        disabled={skipped || isSubmitted}
                                        style={{ width: '100%', display: 'flex' }}
                                      >
                                        <Row gutter={0} style={{ marginBottom: 0, width: '100%' }}>
                                          <Col span={12} style={{ textAlign: 'center' }}>
                                            <Radio value="major">Major</Radio>
                                          </Col>
                                          <Col span={12} style={{ textAlign: 'center' }}>
                                            <Radio value="minor">Minor</Radio>
                                          </Col>
                                        </Row>
                                      </Radio.Group>
                                    </Col>
                                  
                                    <Col span={6}>
                                      <Input
                                        placeholder="Comments"
                                        value={item.comments || ''}
                                        onChange={(e) => handleIssueChange(item.id, 'comments', e.target.value)}
                                        disabled={skipped || isSubmitted}
                                      />
                                    </Col>
                                  </Row>
                            ))}
                        </Checkbox.Group>
                    </>
                )}
            </Spin>

            {/* Fixed Save/Cancel Buttons */}
            <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, display: 'flex', gap: 12 }}>
                <Button onClick={onCancel} size="large" shape="round">
                    Cancel
                </Button>
                <Button onClick={handleSave} type="primary" size="large" shape="round" disabled={isUpdatingEhsLog || isSubmitted || isShiftSkipped} loading={isUpdatingEhsLog}>
                    Save & Next
                </Button>
            </div>

            <AddIssueModal
                visible={isAddModalVisible}
                onCancel={() => setIsAddModalVisible(false)}
                onOk={handleAddIssue}
                allIssues={allEhsIssues}
                issuesLoading={isAddingEhsLogItem}
            />
        </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => ({
    currentEhsLog: dsrReducer.currentEhsLog,
    allEhsIssues: dsrReducer.allEhsIssues,
    isEhsLogLoading: dsrReducer.isEhsLogLoading,
    isUpdatingEhsLog: dsrReducer.isUpdatingEhsLog,
    isAddingEhsLogItem: dsrReducer.isAddingEhsLogItem,
    isDeletingEhsLogItem: dsrReducer.isDeletingEhsLogItem,
});

const mapDispatchToProps = {
    onFetchEhsLog: fetchEhsLog,
    onUpdateEhsLog: updateEhsLog,
    onAddEhsLogItem: addEhsLogItem,
    onDeleteEhsLogItem: deleteEhsLogItem,
    onFetchAllEhsIssues: fetchAllEhsIssues,
    onUpdateDsrLogSections: updateDsrLogSections
};

export default connect(mapStateToProps, mapDispatchToProps)(EHSSection); 